import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { VehicleConfig } from '@/types';
import { DEFAULT_VEHICLE_CONFIGS } from '@/hooks/useStations';

interface VehicleStore {
  vehicleConfig: VehicleConfig;
  isCustomConfig: boolean;
  setVehicleConfig: (config: VehicleConfig) => void;
  setVehicleType: (type: 'petrol' | 'diesel' | 'hybrid' | 'electric') => void;
  setCostPerMile: (costPerMile: number) => void;
  resetToDefault: (type?: 'petrol' | 'diesel' | 'hybrid' | 'electric') => void;
}

/**
 * Store for managing vehicle configuration and fuel cost calculations
 */
export const useVehicleStore = create<VehicleStore>()(
  persist(
    (set, get) => ({
      vehicleConfig: DEFAULT_VEHICLE_CONFIGS.diesel,
      isCustomConfig: false,

      setVehicleConfig: (config) => {
        set({ vehicleConfig: config, isCustomConfig: true });
      },

      setVehicleType: (type) => {
        const defaultConfig = DEFAULT_VEHICLE_CONFIGS[type];
        set({
          vehicleConfig: defaultConfig,
          isCustomConfig: false
        });
      },

      setCostPerMile: (costPerMile) => {
        const currentConfig = get().vehicleConfig;
        set({
          vehicleConfig: { ...currentConfig, costPerMile },
          isCustomConfig: true
        });
      },

      resetToDefault: (type) => {
        const vehicleType = type || get().vehicleConfig.fuelType;
        const defaultConfig = DEFAULT_VEHICLE_CONFIGS[vehicleType];
        set({ 
          vehicleConfig: defaultConfig, 
          isCustomConfig: false 
        });
      },
    }),
    {
      name: 'fuelhunt-vehicle-config',
      version: 1,
    }
  )
);

/**
 * Helper functions for vehicle calculations
 */
export const vehicleUtils = {
  /**
   * Get default vehicle configuration for a fuel type
   */
  getDefaultConfig: (fuelType: 'petrol' | 'diesel' | 'hybrid' | 'electric'): VehicleConfig => {
    return DEFAULT_VEHICLE_CONFIGS[fuelType];
  },

  /**
   * Calculate total travel cost for a round trip
   */
  calculateTravelCost: (distance: number, costPerMile: number): number => {
    return Math.round(distance * 2 * costPerMile); // Round trip
  },


  /**
   * Estimate CO2 emissions for a trip
   * Returns kg of CO2
   */
  calculateCO2Emissions: (distance: number, fuelType: 'petrol' | 'diesel' | 'hybrid' | 'electric'): number => {
    // CO2 emissions per mile (approximate)
    const emissionFactors = {
      petrol: 0.411, // kg CO2 per mile
      diesel: 0.411, // kg CO2 per mile  
      hybrid: 0.200, // kg CO2 per mile
      electric: 0.050, // kg CO2 per mile (including electricity generation)
    };

    return (distance * 2) * emissionFactors[fuelType];
  },


  /**
   * Validate vehicle configuration
   */
  validateConfig: (config: VehicleConfig): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (config.costPerMile < 0 || config.costPerMile > 100) {
      errors.push('Cost per mile must be between 0 and 100 pence');
    }

    if (!['petrol', 'diesel', 'hybrid', 'electric'].includes(config.fuelType)) {
      errors.push('Invalid fuel type');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};